import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { creatorbase, creatorbaseQueries } from '@round/api';
import { showNotification } from 'helpers';
import { CREATORBASE_ROOT_PATH } from 'Modules/Navigation/whitelabel/routes';
import { Button } from '@round/whitelabel-ui';
import { useLinkProjectShareMutation } from '@round/creatorbase-helpers/api-hooks';
import ProjectSelect from 'Modules/Plans/Project/components/ProjectSelect/ProjectSelect';
import useProjectSelect, { ProjectOption } from 'Modules/Plans/Project/hooks/useProjectSelect';
import { inlineProjectSelectStyles } from '../../helpers';
import styles from '../../popout.module.css';

type Props = {
    project: Pick<creatorbase.Project, 'name' | 'project_share_id'>;
    onClose: () => void;
};

const LinkProjectPopout = ({ project, onClose }: Props) => {
    const navigate = useNavigate();
    const { link, isLoading } = useLinkProjectShareMutation();
    const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);
    const { getProps } = useProjectSelect({ initOn: 'mount', params: { owned_by: 'me' } });
    const selectProps = getProps({ onChange: (option) => setSelectedProject(option as ProjectOption | null) });

    const handleLink = async () => {
        if (project.project_share_id === null || !selectedProject) {
            return;
        }

        try {
            await link({ id: project.project_share_id, containerId: selectedProject.value });
            showNotification(`Linked to ${selectedProject.label}`, 'info');
            onClose();
            navigate(`/${CREATORBASE_ROOT_PATH}/projects/${selectedProject.value}/`);
        } catch (error) {
            showNotification(creatorbaseQueries.projectShares.utils.link.getErrorMessage(error as Error), 'error');
        }
    };

    return (
        <>
            <div className={styles.header}>
                <p className={styles.title}>Link to project</p>
            </div>
            <div className={styles.body}>
                <p className={styles.copy}>The campaigns from this project will run inside the project you choose.</p>
                <ProjectSelect
                    {...selectProps}
                    isDisabled={isLoading}
                    autoFocus
                    isClearable
                    menuIsOpen={!selectProps.value || !!selectProps.inputValue}
                    menuPortalTarget={null}
                    tabSelectsValue={false}
                    components={{ DropdownIndicator: null }}
                    styles={inlineProjectSelectStyles}
                    aria-label="Project to link to"
                    placeholder="Search your projects"
                />
            </div>
            <div className={styles.actions}>
                <Button appearance="ghost" disabled={isLoading} onClick={onClose}>
                    Cancel
                </Button>
                <Button appearance="primary" isLoading={isLoading} disabled={!selectedProject} onClick={handleLink}>
                    Link
                </Button>
            </div>
        </>
    );
};

export default LinkProjectPopout;
